import { home } from "../content/home";
import { useLoopTime } from "./useLoopTime";

/**
 * "How it works" step 5: the dashboard's meeting notes page, as at the end of the hero film. The summary
 * types itself out, then the action items land one by one with owner and deadline, then it starts again.
 * Viewers who turn motion off see the finished page, still. Decorative: the step text is the real content.
 */

// ms from the start of a loop.
export const NOTES_TIMELINE = { typeStart: 500, typeEnd: 4200, actions: [4600, 5100, 5600], loop: 10000 } as const;

const notes = home.howItWorks.notes;
const SUMMARY = notes.summary.join("\n\n");

// Dashboard tokens (app.css): paper, near-black ink, Intakall azure.
const C = { bg: "#fcfaf5", surface: "#f6f1e7", raised: "#fffdf9", hairline: "rgb(38 37 36 / 0.12)", muted: "rgb(38 37 36 / 0.72)", brand: "#006fbd" };

function typedLength(t: number) {
  if (t >= NOTES_TIMELINE.typeEnd) return SUMMARY.length;
  if (t <= NOTES_TIMELINE.typeStart) return 0;
  const share = (t - NOTES_TIMELINE.typeStart) / (NOTES_TIMELINE.typeEnd - NOTES_TIMELINE.typeStart);
  return Math.floor(share * SUMMARY.length);
}

export function NotesScreen() {
  const t = useLoopTime(NOTES_TIMELINE.loop);
  const typed = SUMMARY.slice(0, typedLength(t));

  return (
    <div className="flex h-full min-h-[390px] flex-col px-4 py-3 font-sans text-char sm:min-h-0 sm:px-6 sm:py-4" style={{ background: C.bg }}>
      <p className="font-mono text-[10px] uppercase tracking-wider text-char/50">Meeting notes</p>
      <p className="mb-2.5 mt-0.5 flex items-baseline gap-2">
        <span className="font-serif text-[20px] leading-tight">{notes.title}</span>
        <span className="font-mono text-[10px] text-char/55">{notes.when}</span>
      </p>

      <div className="flex-1 rounded-xl border border-char p-3.5 shadow-[-3px_4px_0_0_#262524] sm:px-4 sm:py-3" style={{ background: C.raised }}>
        <p className="mb-1 font-serif text-[17px] font-medium leading-tight">Summary</p>
        {/* The full text sits invisibly underneath, so the card doesn't grow as it types. */}
        <div className="relative text-[12px] leading-[1.45]">
          <p className="invisible whitespace-pre-line">{SUMMARY}</p>
          <p data-testid="notes-summary-typed" className="absolute inset-0 whitespace-pre-line">
            {typed}
          </p>
        </div>
        <div className="mt-2.5 border-t pt-2" style={{ borderColor: C.hairline }}>
          <p className="mb-1.5 text-[10px] font-semibold uppercase tracking-[0.12em]" style={{ color: C.brand }}>
            Action items
          </p>
          <ul className="space-y-1">
            {notes.actionItems.map((item, i) => {
              // Every row keeps its space from the start, so the tablet never changes height.
              const landed = t >= NOTES_TIMELINE.actions[i];
              return (
              <li
                key={item.task}
                data-testid={landed ? "notes-action" : undefined}
                data-task={item.task}
                aria-hidden={!landed}
                className={landed ? "flex items-start gap-2 text-[12px] leading-snug motion-safe:animate-card-slide" : "invisible flex items-start gap-2 text-[12px] leading-snug"}
              >
                <span style={{ color: C.brand }}>›</span>
                <span className="flex-1">
                  {item.task}
                  <span
                    className="ml-1.5 inline-flex rounded-full border px-2 py-px align-middle text-[10.5px] font-semibold"
                    style={{ borderColor: C.hairline, background: C.surface, color: C.muted }}
                  >
                    {item.owner}
                  </span>
                  <span className="ml-1.5 align-middle font-mono text-[10.5px] text-char/60">{item.due}</span>
                </span>
              </li>
              );
            })}
          </ul>
        </div>
      </div>
    </div>
  );
}
